/**
 * VehiclesView - Full-page view for vehicles management
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Add, Refresh, Search } from "@mui/icons-material";
import { Tag, Space, Table, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { getVehicles } from "../services/vehicleService";
import { getPartners } from "../../partners/services/partnerService";
import VehicleForm from "../components/VehicleForm";
import {
  PageShell,
  PageHeader,
  PageFilters,
} from "../../../shared/components/pageShell";

const { Text } = Typography;

/**
 * VehiclesView Component
 */
const VehiclesView = () => {
  // State
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editVehicle, setEditVehicle] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [filters, setFilters] = useState({
    vehicleType: "",
    ownerType: "",
    partnerId: "",
    status: "",
    search: "",
  });
  const [partners, setPartners] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch vehicles
  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getVehicles(filters);
      if (result.success) {
        let vehicleList = result.data || [];
        
        // Apply search filter
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          vehicleList = vehicleList.filter(
            (vehicle) =>
              vehicle.licensePlate?.toLowerCase().includes(searchLower) ||
              vehicle.make?.toLowerCase().includes(searchLower) ||
              vehicle.model?.toLowerCase().includes(searchLower)
          );
        }
        
        setVehicles(vehicleList);
      } else {
        setError(result.error || "Failed to fetch vehicles");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch vehicles");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // Load partners for filter
  useEffect(() => {
    const loadPartners = async () => {
      try {
        const result = await getPartners({ isActive: true });
        if (result.success) {
          setPartners(result.data || []);
        }
      } catch (err) {
        console.error("Error loading partners:", err);
      }
    };
    loadPartners();
  }, []);

  // Handlers
  const handleCreateVehicle = useCallback(() => {
    setEditVehicle(null);
    setFormOpen(true);
  }, []);

  const handleEditVehicle = useCallback((vehicle) => {
    setEditVehicle(vehicle);
    setFormOpen(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setFormOpen(false);
    setEditVehicle(null);
  }, []);

  const handleVehicleSaved = useCallback(() => {
    setFormOpen(false);
    setEditVehicle(null);
    fetchVehicles();
    setNotification({
      open: true,
      message: editVehicle ? "Vehicle updated successfully" : "Vehicle created successfully",
      severity: "success",
    });
  }, [editVehicle, fetchVehicles]);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Table columns
  const columns = [
    {
      title: "License Plate",
      dataIndex: "licensePlate",
      key: "licensePlate",
      render: (plate) => <Text strong code>{plate || "N/A"}</Text>,
    },
    {
      title: "Make & Model",
      key: "makeModel",
      render: (_, record) => (
        <Text>{record.make} {record.model} ({record.year})</Text>
      ),
    },
    {
      title: "Type",
      dataIndex: "vehicleType",
      key: "vehicleType",
      render: (type) => (
        <Tag color={type === "large" ? "red" : type === "medium" ? "orange" : "blue"}>
          {type?.toUpperCase() || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Capacity",
      dataIndex: "capacity",
      key: "capacity",
      render: (capacity) => <Text>{capacity ? `${capacity} kg` : "N/A"}</Text>,
    },
    {
      title: "Owner Type",
      dataIndex: "ownerType",
      key: "ownerType",
      render: (type, record) => (
        <Space>
          <Tag color={type === "bubakii" ? "blue" : "orange"}>
            {type === "bubakii" ? "Bubakii" : "3rd Party"}
          </Tag>
          {type === "third_party" && record.partner?.name && (
            <Text type="secondary" style={{ fontSize: "12px" }}>
              ({record.partner.name})
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const colorMap = {
          available: "green",
          in_use: "blue",
          maintenance: "orange",
          unavailable: "red",
        };
        return (
          <Tag color={colorMap[status] || "default"}>
            {status?.replace("_", " ").toUpperCase() || "N/A"}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditVehicle(record)}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Error: {error}</Alert>
      </Box>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title="Vehicles"
        actions={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateVehicle}
            sx={{
              width: { xs: "100%", sm: "auto" },
              flex: { xs: 1, md: "none" },
              textTransform: "none",
              fontWeight: 600,
              minHeight: { xs: 44, md: 36 },
            }}
          >
            Add Vehicle
          </Button>
        }
      />

      <PageFilters>
        <TextField
          placeholder="Search vehicles..."
          value={filters.search}
          onChange={(e) => handleFilterChange("search", e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: { xs: "100%", sm: 250 }, bgcolor: "background.paper" }}
        />
        <FormControl
          size="small"
          sx={{ minWidth: { xs: "100%", sm: 150 }, bgcolor: "background.paper" }}
        >
          <InputLabel>Vehicle Type</InputLabel>
          <Select
            value={filters.vehicleType}
            label="Vehicle Type"
            onChange={(e) => handleFilterChange("vehicleType", e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="small">Small</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="large">Large</MenuItem>
          </Select>
        </FormControl>
        <FormControl
          size="small"
          sx={{ minWidth: { xs: "100%", sm: 150 }, bgcolor: "background.paper" }}
        >
          <InputLabel>Owner Type</InputLabel>
          <Select
            value={filters.ownerType}
            label="Owner Type"
            onChange={(e) => {
              handleFilterChange("ownerType", e.target.value);
              if (e.target.value !== "third_party") {
                handleFilterChange("partnerId", "");
              }
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="bubakii">Bubakii</MenuItem>
            <MenuItem value="third_party">3rd Party</MenuItem>
          </Select>
        </FormControl>
        {filters.ownerType === "third_party" && (
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              bgcolor: "background.paper",
            }}
          >
            <InputLabel>Partner</InputLabel>
            <Select
              value={filters.partnerId}
              label="Partner"
              onChange={(e) => handleFilterChange("partnerId", e.target.value)}
            >
              <MenuItem value="">All Partners</MenuItem>
              {partners.map((partner) => (
                <MenuItem key={partner.id} value={partner.id}>
                  {partner.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl
          size="small"
          sx={{ minWidth: { xs: "100%", sm: 150 }, bgcolor: "background.paper" }}
        >
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status}
            label="Status"
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="available">Available</MenuItem>
            <MenuItem value="in_use">In Use</MenuItem>
            <MenuItem value="maintenance">Maintenance</MenuItem>
            <MenuItem value="unavailable">Unavailable</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchVehicles}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Refresh
        </Button>
      </PageFilters>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table
            columns={columns}
            dataSource={vehicles}
            rowKey="id"
            size="small"
            scroll={{ x: "max-content" }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} vehicles`,
            }}
          />
        </Box>
      )}

      <VehicleForm
        open={formOpen}
        vehicle={editVehicle}
        onClose={handleCloseForm}
        onSave={handleVehicleSaved}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default VehiclesView;

